(ns dre.components.search-results.views.article-meta
  (:require
   ["dayjs" :as dayjs]
   [clojure.set :refer [intersection]]
   [clojure.string :as str]
   [dre.app.router :as router :refer [->route]]
   [dre.coll :refer [provide-vector distinct-by]]
   [dre.components.icon :refer [fa-icon]]
   [dre.components.tooltip :refer [tooltip-wrapper]]
   [dre.utils :refer [resolve-url not-blank]]))

(def component-name :results-article-meta)

(defn pmid-uri
  [pmid]
  (str "https://www.ncbi.nlm.nih.gov/pubmed/" pmid))

(defn embase-uri
  [pmid]
  (str "https://www.embase.com/" pmid))


(defn pmcid-uri
  [pmcid]
  (str "https://www.ncbi.nlm.nih.gov/labs/pmc/articles/" pmcid))


(defn nih-uri
  [application-id]
  (str "https://reporter.nih.gov/project-details/" application-id))


(defn patent-uri
  [patent-id]
  (str "https://patents.google.com/patent/"
       (str/replace patent-id "-" "")))


(defn published
  [{:keys [published]}]
  (let [mmddyy (.format (dayjs published) "MMMM D. YYYY")]
    [tooltip-wrapper mmddyy [:span
                             mmddyy]]))


(defn author-short
  [{:keys [name]}]
  (let [{:keys [given-names surnames]} name]
    (str (first surnames) " "
         (apply str (map first (concat given-names (rest surnames)))))))


(defn authors-short
  [authors]
  (if-not (empty? authors)
    (let [et-al? (< 3 (count authors))
          authors (take 3 authors)]
      (str (str/join ", " (map author-short authors))
           (when et-al? ", et al.")))
    "Unknown"))


(defn article-authors
  [{:keys [persons]}]
  (let [primary-roles #{"overall-official"
                        "first"
                        "second"}
        primary? (fn [author]
                   (if (> (count
                            (intersection
                              primary-roles
                              (into #{} (get author :roles)))) 0) 0 1))]
    (seq (sort-by primary? (provide-vector persons)))))

(def article-types
  (let [default-icon :hashtag
        default-color "#67ba4a"]
    {"predicted/D016430"
     {:color "#ebc232"
      :short "CS"
      :icon :magic
      :name "Clinical Study"}
     "predicted/D016449"
     {:color "#ebc232"
      :short "RCT"
      :icon :magic
      :name "Randomized Clinical Trial"}
     "predicted/rwe"
     {:color "#ebc232"
      :short "RWE"
      :icon :magic
      :name "Real-world Evidence"}
     "predicted/D006801"
     {:color "#ebc232"
      :short "HS"
      :icon :magic
      :name "Human-related"}
     "predicted/drug_utilization_postfilter"
     {:color "#ebc232"
      :short "DU"
      :icon :magic
      :name "Treatment Patterns"}
     "predicted/healthcare_utilization_postfilter"
     {:color "#ebc232"
      :short "HU"
      :icon :magic
      :name "Healthcare Economics"}
     "predicted/natural_history_postfilter"
     {:color "#ebc232"
      :short "NH"
      :icon :magic
      :name "Epidemiology"}
     "predicted/risk_postfilter"
     {:color "#ebc232"
      :short "R"
      :icon :magic
      :name "Risk/Prognosis"}
     "predicted/safety_postfilter"
     {:color "#ebc232"
      :short "S"
      :icon :magic
      :name "Efficacy/Safety"}
     "predicted/incidence_prevalence"
     {:color "#ebc232"
      :short "S"
      :icon :magic
      :name "Incidence/Prevalence"}
     "pubtype/D016430"
     {:color  default-color
      :short "CS"
      :icon default-icon
      :name "PubMed Clinical Study"}
     "pubtype/D016449"
     {:color default-color
      :short "RCT"
      :icon default-icon
      :name "PubMed Randomized Controlled Trial"}
     "pubtype/D017418"
     {:color default-color
      :short "MA"
      :icon default-icon
      :name "PubMed Meta-Analysis"}
     "pubtype/D016454"
     {:color default-color
      :short "R"
      :icon default-icon
      :name "PubMed Review"}
     "pubtype/D002363"
     {:color default-color
      :short "CR"
      :icon default-icon
      :name "PubMed Case Report"}
     "pubtype/D064888"
     {:color default-color
      :short "OS"
      :icon default-icon
      :name "PubMed Observational Study"}
     "pubtype/D016431"
     {:color default-color
      :short "HG"
      :icon default-icon
      :name "PubMed Healthcare Guideline"}
     "pubtype/D017065"
     {:color default-color
      :short "PG"
      :icon default-icon
      :name "PubMed Practice Guideline"}
     "epar-status/refused"
     {:color default-color
      :short "RF"
      :icon default-icon
      :name "Refused Drug"}
     "epar-status/withdrawn"
     {:color default-color
      :short "WD"
      :icon default-icon
      :name "Withdrawn Drug"}
     "epar-status/authorised"
     {:color default-color
      :short "A"
      :icon default-icon
      :name "Authorized Drug"}
     "phase/early-phase1"
     {:color default-color
      :short "PE1"
      :icon default-icon
      :name "Early phase 1"}
     "phase/phase1"
     {:color default-color
      :short "P1"
      :icon default-icon
      :name "Phase 1"}
     "phase/phase1-2"
     {:color default-color
      :short "P1-2"
      :icon default-icon
      :name "Phase 1-2"}
     "phase/phase2"
     {:color default-color
      :short "P2"
      :icon default-icon
      :name "Phase 2"}
     "phase/phase2-3"
     {:color default-color
      :short "P2-3"
      :icon default-icon
      :name "Phase 2-3"}
     "phase/phase3"
     {:color default-color
      :short "P3"
      :icon default-icon
      :name "Phase 3"}
     "phase/phase4"
     {:color default-color
      :short "P4"
      :icon default-icon
      :name "Phase 4"}
     "curated/animal"
     {:color default-color
      :short "A"
      :icon default-icon
      :name "Animal"}
     "curated/in-vitro"
     {:color default-color
      :short "IV"
      :icon default-icon
      :name "In-Vitro"}
     "curated/non-clinical-study"
     {:color default-color
      :short "NCS"
      :icon default-icon
      :name "Non-clinical study"}
     "curated/review-or-meta-analysis"
     {:color default-color
      :short "RMA"
      :icon default-icon
      :name "Review/Meta Analysis"}
     "curated/retractions-or-errata"
     {:color default-color
      :short "ROE"
      :icon default-icon
      :name "Retractions/Errata"}
     "curated/diagnosis-accuracy"
     {:color default-color
      :short "DA"
      :icon default-icon
      :name "Diagnosis Accuracy"}}))

(defn participants
  [doc]
  (or (:article/participants doc)
      (get-in doc [:article/meta :participants])))

(defn long-article-pills
  [doc]
  (let [types article-types
        tags (:article/tags doc)
        tags (keep (fn [article-tag]
                     (when (contains? types article-tag)
                       article-tag))
                   tags)

        pf #(hash-map :pill/type :tag
                      :pill/query %
                      :pill/source (first (str/split (name %) #"/"))
                      :pill/color (get-in types [% :color])
                      :pill/name (get-in types [% :name])
                      :pill/icon (get-in types [% :icon])
                      :pill/label (get-in types [% :short]))]
    (map pf tags)))

(defn- label
  [content color]
  [:span
   {:class :label
    :style {:background-color color}}
   content])

(defn long-article-tags
  [doc]
  [:span
   {:class component-name}
   (for [p (long-article-pills doc)]
     ^{:key (:pill/query p)}
     [label
      [:<>
       [fa-icon (:pill/icon p)]
       (:pill/name p)]
      (:pill/color p)])
   (when-let [n (participants doc)]
     [label (str "n=" n) "#dc9a30"])])

(defn render-source-pills-for-sources-placeholder
  [{:article/keys [sources]}]
  (let [->human-readable #(get {"medline" "PubMed"
                                "pmc" "PubMed Central"
                                "cord19" "CORD-19"} %)]
    [:span
     {:class component-name}
     (->> sources
          (map ->human-readable)
          (map (fn [source]
                 [label
                  [:<>
                   [fa-icon :inbox]
                   source]
                  "#16a2c0"])))]))

(defn pubmed-overview
  [{:keys [meta source uri] :as doc}]
  (let [authors (article-authors doc)
        pmid (when (= "medline" source) uri)]
    [:span
     "PubMed: "
     [:a {:target "_blank"
          :href (pmid-uri pmid)}
      pmid]
     (when-let [doi (:doi meta)]
       [:span ", doi: " [:a {:target "_blank"
                             :href (str "http://doi.org/" doi)}
                         doi]])
     (when-let [journal (get-in meta [:journal :iso-abbrv])]
       [:em " " journal])
     (when authors
       [:span (str "; " (authors-short authors))])
     " — " [published doc]]))


(defn ccc-overview
  [{:keys [meta] :as doc}]
  (let [authors (article-authors doc)]
    [:span
     "CCC"
     (when-let [doi (:doi meta)]
       [:span ", doi: " [:a {:target "_blank"
                             :href (str "http://doi.org/" doi)}
                         doi]])
     (when-let [journal (get-in meta [:journal :iso-abbrv])]
       [:em " " journal])
     (when authors
       [:span (str "; " (authors-short authors))])
     " — " [published doc]]))


(defn embase-overview
  [{:keys [meta source uri] :as doc}]
  (let [authors (article-authors doc)
        pui (when (= "embase" source) uri)]
    [:span
     "Embase: "
     [:a {:target "_blank"
          :href (embase-uri pui)}
      pui]
     (when-let [doi (:doi meta)]
       [:span ", doi: " [:a {:target "_blank"
                             :href (str "http://doi.org/" doi)}
                         doi]])
     (when-let [journal (get-in meta [:journal :iso-abbrv])]
       [:em " " journal])
     (when authors
       [:span (str "; " (authors-short authors))])
     " — " [published doc]]))


(defn pmc-overview
  [{:keys [meta source uri] :as doc}]
  (let [authors (article-authors doc)
        pmcid (when (= "pmc" source) uri)]
    [:span
     "PubMed Central: "
     [:a {:target "_blank"
          :href (pmcid-uri pmcid)}
      pmcid]
     (when-let [pmid (:pmid meta)]
       [:span ", PMID: " [:a {:target "_blank"
                         :href (pmid-uri pmid)}
                     pmid]])
     (when-let [doi (:doi meta)]
       [:span ", doi: " [:a {:target "_blank"
                             :href (str "http://doi.org/" doi)}
                         doi]])
     (when-let [journal (get-in meta [:journal :iso-abbrv])]
       [:em " " journal])
     (when authors
       [:span (str "; " (authors-short authors))])
     " — " [published doc]]))


(defn nih-overview
  [{:keys [meta source uri] :as doc}]
  (let [authors (article-authors doc)
        application-id (when (= "nih" source)
                         (first (:application-ids meta)))
        core-project-num uri]
    [:span
     "NIH Grant: "
     [:a {:target "_blank"
          :href (nih-uri application-id)}
      core-project-num]
     (when-let [names (seq (:org-names meta))]
       [:em " " (str/join "; " names)])
     (when authors
       [:span (str "; " (authors-short authors))])
     " — " [published doc]]))


(defn patent-overview
  [{:keys [meta uri] :as doc}]
  (let [{:keys [inventor assignee]} meta]
    [:span
     "Patent: "
     [:a {:target "_blank"
          :href (patent-uri uri)}
      uri]
     [:span (str (when inventor (str "; " inventor))
                 (when assignee (str "; " assignee)))]
     " — " [published doc]]))


(defn epar-pdf-overview
  [{:keys [uri] :as doc}]
  [:span
   [:a {:target "_blank"
        :href uri} "EPAR Document"]
   " — " [published doc]])


(defn nct-uri
  [nct]
  (str "https://clinicaltrials.gov/study/" nct))

(defn clinicaltrials-overview
  [{:keys [uri] :as doc}]
  (let [authors (article-authors doc)]
    [:span
     "ClinicalTrials.gov: "
     [:a {:target "_blank"
          :href (nct-uri uri)}
      uri]
     (when authors
       [:span (str "; " (authors-short authors))])
     " — " [published doc]]))


(defn feed-overview
  [{:keys [meta category source] :as doc}]
  (let [authors (article-authors doc)]
    [:span
     [:a {:href source :target "_blank"}]
     [:a {:href (when (:feed-link meta)
                  (resolve-url
                    (:feed-link meta) (:link meta))) :target "_blank"}
      (:feed-title meta)] " (" category ")"
     (when authors
       [:span (str "; " (authors-short authors))])
     " — " [published doc]]))


(defn cord19-overview
  [{:keys [persons meta category] :as doc}]
  (let [authors (seq (provide-vector persons))]
    [:span
     "CORD19: "
     category
     (when-let [doi (not-blank (:doi meta))]
       (let [doi (if-let [idx (str/index-of doi "doi.org/")]
                   (subs doi (+ idx 8))
                   doi)]
         [:span ", doi: " [:a {:target "_blank"
                               :href (str "https://doi.org/" doi)}
                           doi]]))
     (when authors
       [:span (str " " (authors-short authors))])  " — " [published doc]]))


(defn dailymed-uri
  [uri]
  (str "https://dailymed.nlm.nih.gov/dailymed/drugInfo.cfm?setid=" uri))


(defn dailymed-overview
  [doc]
  [:span
   "DailyMed"
   " - "
   [published doc]])


(defn epar-overview
  [{:keys [meta] :as doc}]
  [:span
   [:a {:href (:full-link meta)
        :target "_blank"} "EPAR"]
   " - "
   [published doc]])


(defn who-ictrp-overview
  [{:keys [meta persons] :as doc}]
  (let [authors (seq (provide-vector persons))]
    [:span
     "WHO ICTRP: "
     (if-let [link (or (not-blank (:full-link meta))
                       (not-blank (:external-url meta)))]
       [:a {:href link
            :target "_blank"} (:main-id meta)]
       [:span (:main-id meta)])

     (when authors
       [:span (str "; " (authors-short authors))])
     " - "
     [published doc]]))


(defn collections-overview
  [{:keys [persons] :as doc}]
  (let [authors (seq (provide-vector persons))
        filename (-> doc :meta :source-filename)
        idx (when-not (str/blank? filename)
              (str/last-index-of filename "."))]
    [:span
     (-> doc :meta :collection-name) " (private collection) "
     (when authors
       [:span " — " (str (authors-short authors))])
     (when idx
       [:span " — " (subs filename 0 idx)])
     " — "
     [published doc]]))


(defn doc-congress-overview
  [{:keys [persons source] :as doc}]
  (let [authors (seq (provide-vector persons))]
    [:span
     "Congresses (" (second (str/split source ":")) "): "
     (when authors
       [:span (str (authors-short authors))])  " — " [published doc]]))


(defn doc-congress-dev-overview
  [{:keys [persons source] :as doc}]
  (let [authors (seq (provide-vector persons))]
    [:span
     "CongressesDev" " (" (second (str/split source ":")) "): "
     (when authors
       [:span (str (authors-short authors))])  " — " [published doc]]))

(defn format-related-uri
  [uri]
  (str/replace uri #"^http://www.who.int/trialsearch/Trial3.aspx\?trialid=" ""))

(defn related
  [doc]
  (when-let [relations (:relations doc)]
    (let [related (distinct-by :target/guid (seq relations))]
      [:div
       [fa-icon :link]
       [:span " Related: "
        (butlast
         (for [rel (interleave
                    (take 5 related)
                    (range))]
           (if (integer? rel)
             ", "
             ^{:key (:target/guid rel)}
             [:a {:href (->route :detail {:id (:target/guid rel)})}
              (format-related-uri (:target/uri rel))])))
        (when (> (count related) 5) ",…")]])))

(defn view
  [doc]
  (case (:category doc)
    "medline" [pubmed-overview doc]
    "clinicaltrials" [clinicaltrials-overview doc]
    "epar" [epar-overview doc]
    "who-ictrp" [who-ictrp-overview doc]
    "dailymed" [dailymed-overview doc]
    "doc-congress" [doc-congress-overview doc]
    "doc-congress-dev" [doc-congress-dev-overview doc]
    "collections" [collections-overview doc]
    "cord19" [cord19-overview doc]
    "pmc" [pmc-overview doc]
    "embase" [embase-overview doc]
    "ccc" [ccc-overview doc]
    "nih" [nih-overview doc]
    "patent" [patent-overview doc]
    "epar-pdf" [epar-pdf-overview doc]
    [feed-overview doc]))
